import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

const Navbar: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const location = useLocation();
  const navigate = useNavigate();

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const toggleDropdown = (dropdown: string) => {
    setActiveDropdown(activeDropdown === dropdown ? null : dropdown);
  };

  const closeMenu = () => {
    setIsOpen(false);
    setActiveDropdown(null);
  };

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
  };

  const handleNavigate = (path: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    closeMenu();
    navigate(path);
  };

  return (
    <div className="bg-primary sticky top-0 z-50 shadow-lg">
      <div className="container mx-auto px-4">
        <nav className="flex justify-between items-center py-4">
          {/* Logo and Brand */}
          <Link to="/" className="flex items-center space-x-3" onClick={closeMenu}>
            <img 
              src="/img/logo.png" 
              alt="Nexedz Logo" 
              className="h-12 w-12 object-contain"
            />
            <div className="flex flex-col">
              <div className="text-2xl sm:text-3xl font-bold">
                <span className="text-white">NEX</span>
                <span className="text-secondary">EDZ</span>
              </div>
              <p className="text-xs text-gray-200 tracking-wide">
                Innovation • Education • Empowerment
              </p>
            </div>
          </Link>

          {/* Mobile Menu Button */}
          <button
            className="xl:hidden text-white focus:outline-none z-50"
            onClick={() => setIsOpen(!isOpen)}
            aria-label="Toggle menu"
          >
            {isOpen ? (
              <i className="fas fa-times text-3xl"></i>
            ) : (
              <i className="fas fa-bars text-3xl"></i>
            )}
          </button>

          {/* Desktop Menu */}
          <div className="hidden xl:flex items-center space-x-6 absolute left-1/2 transform -translate-x-1/2">
            <Link
              to="/"
              className={`text-lg transition duration-300 ${
                isActive('/') ? 'text-secondary' : 'text-white hover:text-secondary'
              }`}
            >
              Home
            </Link>

            {/* About Dropdown */}
            <div className="relative group">
              <a
                href="#"
                onClick={handleClick}
                className={`text-lg transition duration-300 flex items-center cursor-pointer ${
                  isActive('/about') || isActive('/who-we-are') ? 'text-secondary' : 'text-white hover:text-secondary'
                }`}
              >
                About Us
                <i className="fas fa-chevron-down ml-1 text-xs"></i>
              </a>
              <ul className="absolute left-0 top-full mt-2 w-56 bg-white shadow-lg rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 z-50">
                <li>
                  <a
                    href="/who-we-are"
                    onClick={handleNavigate('/who-we-are')}
                    className="block px-6 py-3 hover:bg-gray-50 hover:text-secondary transition rounded-t-lg cursor-pointer"
                  >
                    Who We Are
                  </a>
                </li>
              </ul>
            </div>

            {/* Education Dropdown */}
            <div className="relative group">
              <a
                href="#"
                onClick={handleClick}
                className={`text-lg transition duration-300 flex items-center cursor-pointer ${
                  isActive('/education') || isActive('/edge-infotech') ? 'text-secondary' : 'text-white hover:text-secondary'
                }`}
              >
                Education
                <i className="fas fa-chevron-down ml-1 text-xs"></i>
              </a>
              <ul className="absolute left-0 top-full mt-2 w-56 bg-white shadow-lg rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 z-50">
                <li>
                  <a
                    href="/edge-infotech"
                    onClick={handleNavigate('/edge-infotech')}
                    className="block px-6 py-3 hover:bg-gray-50 hover:text-secondary transition rounded-lg cursor-pointer"
                  >
                    EDGE INFOTECH
                  </a>
                </li>
              </ul>
            </div>

            {/* Services for Bharat Dropdown */}
            <div className="relative group">
              <a
                href="#"
                onClick={handleClick}
                className={`text-lg transition duration-300 flex items-center cursor-pointer ${
                  isActive('/services-for-bharat') || isActive('/edge-paisa') || isActive('/rojgar-mitra') ? 'text-secondary' : 'text-white hover:text-secondary'
                }`}
              >
                Services for Bharat
                <i className="fas fa-chevron-down ml-1 text-xs"></i>
              </a>
              <ul className="absolute left-0 top-full mt-2 w-56 bg-white shadow-lg rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 z-50">
                <li>
                  <a
                    href="/edge-paisa"
                    onClick={handleNavigate('/edge-paisa')}
                    className="block px-6 py-3 hover:bg-gray-50 hover:text-secondary transition rounded-t-lg cursor-pointer"
                  >
                    Edge Paisa
                  </a>
                </li>
                <li>
                  <a
                    href="/rojgar-mitra"
                    onClick={handleNavigate('/rojgar-mitra')}
                    className="block px-6 py-3 hover:bg-gray-50 hover:text-secondary transition rounded-b-lg cursor-pointer"
                  >
                    Rojgar Mitra
                  </a>
                </li>
              </ul>
            </div>

            <a
              href="/careers"
              onClick={handleNavigate('/careers')}
              className={`text-lg transition duration-300 cursor-pointer ${
                isActive('/careers') ? 'text-secondary' : 'text-white hover:text-secondary'
              }`}
            >
              Careers
            </a>

            <a
              href="/contact"
              onClick={handleNavigate('/contact')}
              className={`text-lg transition duration-300 cursor-pointer ${
                isActive('/contact') ? 'text-secondary' : 'text-white hover:text-secondary'
              }`}
            >
              Contact Us
            </a>
          </div>

          {/* Contact Phone Section */}
          <div className="hidden xl:flex items-center space-x-3">
            <div className="relative">
              <i className="fas fa-phone-alt text-white text-2xl"></i>
              <span className="absolute -top-1 -right-1 bg-blue-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                24
              </span>
            </div>
            <div className="flex flex-col">
              <span className="text-xs text-gray-200">Have any questions?</span>
              <a href="tel:+919501513413" className="text-secondary font-semibold hover:text-white transition">
                Call: +91 9501513413
              </a>
            </div>
          </div>
        </nav>

        {/* Mobile Menu */}
        {isOpen && (
          <div className="xl:hidden pb-4 space-y-2 max-h-screen overflow-y-auto">
            <Link 
              to="/" 
              className="block text-white hover:text-secondary py-3 px-4 transition rounded-lg hover:bg-white/10"
              onClick={closeMenu}
            >
              Home
            </Link>

            {/* About Dropdown Mobile */}
            <div>
              <button
                onClick={() => toggleDropdown('about')}
                className="w-full text-left text-white hover:text-secondary py-3 px-4 transition flex justify-between items-center rounded-lg hover:bg-white/10"
              >
                About Us
                <i className={`fas fa-chevron-${activeDropdown === 'about' ? 'up' : 'down'} text-sm`}></i>
              </button>
              {activeDropdown === 'about' && (
                <div className="pl-4 space-y-1">
                  <a 
                    href="/who-we-are"
                    onClick={handleNavigate('/who-we-are')}
                    className="block text-white hover:text-secondary py-2 px-4 transition text-sm cursor-pointer"
                  >
                    └─ Who We Are
                  </a>
                </div>
              )}
            </div>

            {/* Education Dropdown Mobile */}
            <div>
              <button
                onClick={() => toggleDropdown('education')}
                className="w-full text-left text-white hover:text-secondary py-3 px-4 transition flex justify-between items-center rounded-lg hover:bg-white/10"
              >
                Education
                <i className={`fas fa-chevron-${activeDropdown === 'education' ? 'up' : 'down'} text-sm`}></i>
              </button>
              {activeDropdown === 'education' && (
                <div className="pl-4">
                  <a 
                    href="/edge-infotech"
                    onClick={handleNavigate('/edge-infotech')}
                    className="block text-white hover:text-secondary py-2 px-4 transition text-sm cursor-pointer"
                  >
                    └─ EDGE INFOTECH
                  </a>
                </div>
              )}
            </div>

            {/* Services Dropdown Mobile */}
            <div>
              <button
                onClick={() => toggleDropdown('services')}
                className="w-full text-left text-white hover:text-secondary py-3 px-4 transition flex justify-between items-center rounded-lg hover:bg-white/10"
              >
                Services for Bharat
                <i className={`fas fa-chevron-${activeDropdown === 'services' ? 'up' : 'down'} text-sm`}></i>
              </button>
              {activeDropdown === 'services' && (
                <div className="pl-4 space-y-1">
                  <a 
                    href="/edge-paisa"
                    onClick={handleNavigate('/edge-paisa')}
                    className="block text-white hover:text-secondary py-2 px-4 transition text-sm cursor-pointer"
                  >
                    └─ Edge Paisa
                  </a>
                  <a 
                    href="/rojgar-mitra"
                    onClick={handleNavigate('/rojgar-mitra')}
                    className="block text-white hover:text-secondary py-2 px-4 transition text-sm cursor-pointer"
                  >
                    └─ Rojgar Mitra
                  </a>
                </div>
              )}
            </div>

            <a 
              href="/careers"
              onClick={handleNavigate('/careers')}
              className="block text-white hover:text-secondary py-3 px-4 transition rounded-lg hover:bg-white/10 cursor-pointer"
            >
              Careers
            </a>

            <a 
              href="/contact"
              onClick={handleNavigate('/contact')}
              className="block text-white hover:text-secondary py-3 px-4 transition rounded-lg hover:bg-white/10 cursor-pointer"
            >
              Contact Us
            </a>

            {/* Mobile Contact Phone */}
            <div className="flex items-center space-x-3 py-3 px-4 border-t border-white/20 mt-2">
              <div className="relative">
                <i className="fas fa-phone-alt text-white text-xl"></i>
                <span className="absolute -top-1 -right-1 bg-blue-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                  24
                </span>
              </div>
              <div className="flex flex-col">
                <span className="text-xs text-gray-200">Have any questions?</span>
                <a href="tel:+919501513413" className="text-secondary font-semibold">
                  Call: +91 9501513413
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Navbar;